import OtherAck from "../../../classes/OtherAck.js";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { isEqual } from "lodash";

// Acknowledgements that are not for a sale -- see src/classes/OtherAck.js.

// What one for a service issue's parts tells the issue (ORDER_SERVICE_PARTS):
// ordered, under the vendor's number (else our PO), due on the expected date.
// Our PO is kept apart: it stands in only for an issue with no number, and
// never over the vendor's number recorded through Order Parts.
const partsOrder = (data) =>
  data && data.type === "service" && data.serviceIssueID
    ? {
        issueID: data.serviceIssueID,
        orderNumber: data.vendorOrderNumber || "",
        orderPO: data.orderPO || "",
        expectedDate: data.expectedDate || null,
      }
    : null;

export const GET_OTHER_ACK = ({ dispatch }, { ackID }) => {
  return dispatch("GET_DOC", {
    converter: OtherAck,
    path: OtherAck.getPath(ackID),
    stateKey: "otherAcks",
    id: ackID,
  });
};

export const LISTEN_OTHER_ACK = ({ dispatch }, { ackID, next, error }) => {
  return dispatch("LISTEN_DOC", {
    converter: OtherAck,
    path: OtherAck.getPath(ackID),
    stateKey: "otherAcks",
    id: ackID,
    next,
    error,
  });
};

export const LISTEN_OTHER_ACKS = (
  { dispatch },
  { where, listenerPath, limitCount, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: OtherAck,
    stateKey: "otherAcks",
    path: OtherAck.getPath(),
    listenerPath,
    where,
    limitCount,
    next,
    error,
    unlisten,
  });
};

/**
 * Save an acknowledgement, new or edited, with its documents. One for a
 * service issue's parts records the order on the issue -- ordered, under the
 * vendor's number (else our PO), due on the expected date -- so the issue
 * reads "Awaiting parts" and receiving can recognise the parts by it.
 */
export const SAVE_OTHER_ACK = async ({ dispatch, state }, { ack }) => {
  const batch = writeBatch(db);
  const now = new Date();
  let existing = null;

  if (!ack.id) {
    ack.id = doc(collection(db, OtherAck.getPath())).id;
    ack.data.created = now;
    ack.data.createdBy = state.user.id;
  } else {
    existing = await dispatch("GET_OTHER_ACK", { ackID: ack.id });
    ack.data.modified = now;
    ack.data.modifiedBy = state.user.id;
  }

  await dispatch("SAVE_DOCUMENTS", {
    batch,
    parentData: ack.data,
    fieldPath: "documents",
    folderPath: ack.path,
    existingData: existing ? existing.data : undefined,
  });
  batch.set(doc(db, ack.path).withConverter(OtherAck), ack);
  await batch.commit();

  // Only when the issue, the number or the date changed -- a note or a file
  // is no news to the issue -- and an issue it moved off drops what this
  // recorded, so a wrong pick can be undone. The acknowledgement is saved
  // either way: a refused issue update says so without failing the save,
  // which a new one's dialog would retry into a second acknowledgement. The
  // two are tried apart, so a refused one can't skip the other.
  const before = partsOrder(existing && existing.data);
  const after = partsOrder(ack.data);
  if (before && before.issueID !== (after && after.issueID)) {
    try {
      await dispatch("UNLINK_SERVICE_ISSUE_FROM_ORDER", {
        issueID: before.issueID,
        orderNumber: before.orderNumber,
        orderPO: before.orderPO,
      });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
  if (after && !isEqual(before, after)) {
    try {
      // A number it recorded on the issue before is its own to replace.
      const replaces =
        before && before.issueID === after.issueID
          ? [before.orderNumber, before.orderPO]
          : [];
      await dispatch("ORDER_SERVICE_PARTS", { ...after, replaces });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
  return ack;
};

/**
 * Record that an acknowledgement's material arrived: where it was put, and
 * the truck and receiving session it came in on when receiving took it in.
 * One for a service issue's parts marks the issue's parts received too,
 * unless the caller does that itself (`markIssue: false` -- the Complete
 * step lists the issue and marks it with the rest).
 */
export const RECEIVE_OTHER_ACK = async (
  { dispatch, state },
  { ackID, location = "", sessionID = "", truckNumber = "", markIssue = true }
) => {
  const ack =
    state.otherAcks[ackID] || (await dispatch("GET_OTHER_ACK", { ackID }));
  const now = new Date();
  await updateDoc(doc(db, OtherAck.getPath(ackID)), {
    fullyReceived: true,
    receivedDate: now,
    receivedBy: state.user.id,
    receivedLocation: location || "",
    receivedSessionID: sessionID || "",
    receivedTruckNumber: truckNumber || "",
    modified: now,
    modifiedBy: state.user.id,
  });

  if (
    markIssue &&
    ack &&
    ack.data.type === "service" &&
    ack.data.serviceIssueID
  ) {
    await dispatch("RECEIVE_SERVICE_PARTS", {
      issueID: ack.data.serviceIssueID,
      warehouseLocation: location,
      receipt: {
        sessionID,
        truckNumber,
        orderPO: ack.data.orderPO,
        vendorOrderNumber: ack.data.vendorOrderNumber,
      },
    });
  }
};

/**
 * Delete an acknowledgement and its documents. A service issue whose parts
 * order it recorded, and whose parts have not come, drops the number and date
 * it brought (UNLINK_SERVICE_ISSUE_FROM_ORDER).
 */
export const DELETE_OTHER_ACK = async ({ dispatch, state }, { ackID }) => {
  const ack =
    state.otherAcks[ackID] || (await dispatch("GET_OTHER_ACK", { ackID }));
  const order = ack && !ack.data.fullyReceived ? partsOrder(ack.data) : null;

  // Drop the files first: once the doc is gone their names are lost.
  if (ack && ack.data.documents && ack.data.documents.length) {
    await dispatch("SAVE_DOCUMENTS", {
      parentData: { documents: [] },
      existingData: ack.data,
      fieldPath: "documents",
      folderPath: OtherAck.getPath(ackID),
    });
  }
  await deleteDoc(doc(db, OtherAck.getPath(ackID)));

  if (order) {
    try {
      await dispatch("UNLINK_SERVICE_ISSUE_FROM_ORDER", {
        issueID: order.issueID,
        orderNumber: order.orderNumber,
        orderPO: order.orderPO,
      });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
};
